import graphql__42a5b70d2550a71f6bdd from "./__generated__/EditPlannedItemDialogAddMutation.graphql.ts";
import graphql__d11dd8b43d2f08e4fa95 from "./__generated__/EditPlannedItemDialogChangeMutation.graphql.ts";import * as React from "react";
import {graphql} from "react-relay";
import {useRelayEnvironment} from "react-relay/hooks";
import {useIntl} from "react-intl";
import {useFormikContext} from "formik";
import * as yup from "yup";
import FormDialog from "./FormDialog";
import Section from "../form/Section";
import Row from "../form/Row";
import Label from "../form/Label";
import Field from "../form/Field";
import {commitMutationWithErrorHandling} from "../../helper/commitMutationWithErrorHandling";
import {formatCentsForInput, parseAmountToCents, yupAmount} from "../../helper/amountInput";
import {useErrorContext} from "../../contexts/ErrorContext";
import {vatRatePercent, vatRates} from "../../helper/vatRate";
import type {EditPlannedItemDialogAddMutation} from "./__generated__/EditPlannedItemDialogAddMutation.graphql";
import type {EditPlannedItemDialogChangeMutation} from "./__generated__/EditPlannedItemDialogChangeMutation.graphql";

type Props = {
    // Without a planned item, the dialog adds one
    plannedItem?: {
        id: string
        definition: {
            name: string
            category: string
            netAmount: number
            vatRate: string
            rhythm: string
            startMonth: string
            endMonth?: string | null
            dayOfMonth: number
            effect: string
            liquidityGroup?: string | null
        }
    }
    // The month a new item starts with
    defaultStartMonth: string
    onClose: (changed: boolean) => void
}

const categories = ['REVENUE', 'OTHER_OPERATING_INCOME', 'SUPPLIERS', 'PERSONNEL', 'DEPRECIATION', 'INCOME_TAXES', 'FINANCING', 'OTHER'];
const rhythms = ['MONTHLY', 'QUARTERLY', 'YEARLY', 'ONE_TIME'];
const effects = ['RESULT_AND_LIQUIDITY', 'LIQUIDITY_ONLY', 'RESULT_ONLY'];
const liquidityGroups = ['CUSTOMER_PAYMENTS', 'SUPPLIERS', 'SALARIES', 'TAX_OFFICE', 'FINANCING', 'OTHER'];

const monthPattern = /^\d{4}-(0[1-9]|1[0-2])$/;

const addMutation = graphql__42a5b70d2550a71f6bdd;

const changeMutation = graphql__d11dd8b43d2f08e4fa95;

/**
 * An item that is due once has no end month, so the field is left out for it.
 */
const EndMonthRow: React.FC = () => {
    const intl = useIntl();
    const {values} = useFormikContext<{rhythm: string}>();

    if (values.rhythm === 'ONE_TIME') {
        return null;
    }
    return (
        <Row>
            <Label>{intl.formatMessage({id: 'dialog.EditPlannedItemDialog.endMonthLabel'})}</Label>
            <Field name="endMonth" type="text" placeholder="2027-12" data-1p-ignore="true"/>
        </Row>
    );
};

/**
 * Adds a planned item or changes its definition. Whether the item is switched on is not part of
 * the definition and stays as it is.
 */
const EditPlannedItemDialog: React.FC<Props> = ({plannedItem, defaultStartMonth, onClose}) => {
    const intl = useIntl();
    const relayEnvironment = useRelayEnvironment();
    const {addError} = useErrorContext();

    const handleSubmission = (values) => {
        const definitionInput = {
            name: values.name.trim(),
            category: values.category,
            netAmount: parseAmountToCents(values.netAmount) as number,
            vatRate: values.vatRate,
            rhythm: values.rhythm,
            startMonth: values.startMonth.trim(),
            endMonth: values.rhythm === 'ONE_TIME' || values.endMonth.trim() === '' ? null : values.endMonth.trim(),
            dayOfMonth: Number(values.dayOfMonth),
            effect: values.effect,
            liquidityGroup: values.liquidityGroup === '' ? null : values.liquidityGroup,
        };
        const options = {
            variables: {input: plannedItem ? {id: plannedItem.id, definition: definitionInput} : {definition: definitionInput}},
            onCompleted: (_, errors) => {
                if (errors && errors.length > 0) {
                    console.error(errors);
                }
                onClose(true);
            },
            onError: (error) => {
                console.error(error);
                onClose(false);
            },
        };
        const customErrorMessage = intl.formatMessage({id: plannedItem ? 'errors.failedToChangePlannedItem' : 'errors.failedToAddPlannedItem'});
        if (plannedItem) {
            commitMutationWithErrorHandling<EditPlannedItemDialogChangeMutation>(relayEnvironment, {mutation: changeMutation, ...options}, addError, {customErrorMessage});
        } else {
            commitMutationWithErrorHandling<EditPlannedItemDialogAddMutation>(relayEnvironment, {mutation: addMutation, ...options}, addError, {customErrorMessage});
        }
    };

    const validationSchema = yup.object().shape({
        name: yup.string()
            .trim()
            .max(255, intl.formatMessage({id: 'validation.field.nameTooLong'}))
            .required(intl.formatMessage({id: 'validation.field.required'})),
        netAmount: yupAmount(intl),
        startMonth: yup.string()
            .trim()
            .required(intl.formatMessage({id: 'validation.field.required'}))
            .matches(monthPattern, intl.formatMessage({id: 'validation.field.invalidMonth'})),
        endMonth: yup.string()
            .trim()
            .test('month', intl.formatMessage({id: 'validation.field.invalidMonth'}), value => (value ?? '') === '' || monthPattern.test(value ?? ''))
            .test('afterStart', intl.formatMessage({id: 'validation.field.endMonthBeforeStartMonth'}), function (value) {
                return (value ?? '') === '' || !monthPattern.test(this.parent.startMonth ?? '') || !monthPattern.test(value ?? '') || value! >= this.parent.startMonth;
            }),
        dayOfMonth: yup.number()
            .typeError(intl.formatMessage({id: 'validation.field.invalidDayOfMonth'}))
            .integer(intl.formatMessage({id: 'validation.field.invalidDayOfMonth'}))
            .min(1, intl.formatMessage({id: 'validation.field.invalidDayOfMonth'}))
            .max(31, intl.formatMessage({id: 'validation.field.invalidDayOfMonth'}))
            .required(intl.formatMessage({id: 'validation.field.required'})),
    });

    const definition = plannedItem?.definition;

    return (
        <FormDialog handleCloseDialog={() => onClose(false)}
                    handleSubmission={handleSubmission}
                    initialValues={{
                        name: definition?.name ?? '',
                        category: definition?.category ?? 'SUPPLIERS',
                        netAmount: definition ? formatCentsForInput(definition.netAmount) : '',
                        vatRate: definition?.vatRate ?? 'NONE',
                        rhythm: definition?.rhythm ?? 'MONTHLY',
                        startMonth: definition?.startMonth ?? defaultStartMonth,
                        endMonth: definition?.endMonth ?? '',
                        dayOfMonth: String(definition?.dayOfMonth ?? 1),
                        effect: definition?.effect ?? 'RESULT_AND_LIQUIDITY',
                        liquidityGroup: definition?.liquidityGroup ?? '',
                    }}
                    validationSchema={validationSchema}
                    headline={intl.formatMessage({id: plannedItem ? 'dialog.EditPlannedItemDialog.editHeadline' : 'dialog.EditPlannedItemDialog.addHeadline'})}
                    submitLabel={intl.formatMessage({id: 'button.save'})}>
            <Section>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.EditPlannedItemDialog.nameLabel'})}</Label>
                    <Field name="name" type="text" autoFocus={true} data-1p-ignore="true"/>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.EditPlannedItemDialog.categoryLabel'})}</Label>
                    <Field name="category" as="select">
                        {categories.map(category => (
                            <option key={category} value={category}>{intl.formatMessage({id: 'forecastCategory.' + category})}</option>
                        ))}
                    </Field>
                    <p className="text-sm text-gray-500">{intl.formatMessage({id: 'dialog.EditPlannedItemDialog.categoryHint'})}</p>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.EditPlannedItemDialog.netAmountLabel'})}</Label>
                    <Field name="netAmount" type="text" inputMode="decimal" placeholder="-0,00" data-1p-ignore="true"/>
                    <p className="text-sm text-gray-500">{intl.formatMessage({id: 'dialog.EditPlannedItemDialog.netAmountHint'})}</p>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.EditPlannedItemDialog.vatRateLabel'})}</Label>
                    <Field name="vatRate" as="select">
                        {vatRates.map(vatRate => (
                            <option key={vatRate} value={vatRate}>{vatRatePercent(vatRate)} %</option>
                        ))}
                    </Field>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.EditPlannedItemDialog.rhythmLabel'})}</Label>
                    <Field name="rhythm" as="select">
                        {rhythms.map(rhythm => (
                            <option key={rhythm} value={rhythm}>{intl.formatMessage({id: 'plannedItemRhythm.' + rhythm})}</option>
                        ))}
                    </Field>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.EditPlannedItemDialog.startMonthLabel'})}</Label>
                    <Field name="startMonth" type="text" placeholder="2026-11" data-1p-ignore="true"/>
                </Row>
                <EndMonthRow/>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.EditPlannedItemDialog.dayOfMonthLabel'})}</Label>
                    <Field name="dayOfMonth" type="number" min={1} max={31}/>
                    <p className="text-sm text-gray-500">{intl.formatMessage({id: 'dialog.EditPlannedItemDialog.dayOfMonthHint'})}</p>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.EditPlannedItemDialog.effectLabel'})}</Label>
                    <Field name="effect" as="select">
                        {effects.map(effect => (
                            <option key={effect} value={effect}>{intl.formatMessage({id: 'plannedItemEffect.' + effect})}</option>
                        ))}
                    </Field>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.EditPlannedItemDialog.liquidityGroupLabel'})}</Label>
                    <Field name="liquidityGroup" as="select">
                        <option value="">{intl.formatMessage({id: 'dialog.EditPlannedItemDialog.liquidityGroupSameAsCategory'})}</option>
                        {liquidityGroups.map(group => (
                            <option key={group} value={group}>{intl.formatMessage({id: 'bankTransactionGroup.' + group})}</option>
                        ))}
                    </Field>
                    <p className="text-sm text-gray-500">{intl.formatMessage({id: 'dialog.EditPlannedItemDialog.liquidityGroupHint'})}</p>
                </Row>
            </Section>
        </FormDialog>
    );
};

export default EditPlannedItemDialog;
